import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError, AxiosResponse, AxiosInstance } from 'axios';
import {
  MutateWidgetPayload,
  MutationOptions,
  MutationResult,
  QueryResult,
  WidgetDto,
} from '@thingslog/repositories';
import { QueryKeys } from '../enums/QueryKeys';
import { QueryOptions } from '@thingslog/repositories';
import { buildDashboardUrl } from './dashboardUrlUtils';

export function createDashboardWidgetQueryClient(axios: AxiosInstance): DashboardWidgetQueryClient {
  return new DashboardWidgetQueryClientImp(axios);
}

class DashboardWidgetQueryClientImp {
  public constructor(private axios: AxiosInstance) {}

  public useDashboardWidgets = (
    dashboardId: string,
    companyId?: string | number | null,
    options?: QueryOptions<WidgetDto[]>
  ): QueryResult<WidgetDto[]> => {
    return useQuery<WidgetDto[], AxiosError>(
      [QueryKeys.GetWidgets, dashboardId, companyId],
      () => this.getDashboardWidgets(dashboardId, companyId),
      options
    );
  };

  public useDashboardWidget = (
    dashboardId: string,
    widgetId: string,
    companyId?: string | number | null,
    options?: QueryOptions<WidgetDto>
  ): QueryResult<WidgetDto> => {
    return useQuery<WidgetDto, AxiosError>(
      [QueryKeys.GetWidget, dashboardId, widgetId, companyId],
      () => this.getDashboardWidget(dashboardId, widgetId, companyId),
      options
    );
  };

  public useCreateDashboardWidget = (
    options?: MutationOptions<
      WidgetDto,
      { dashboardId: string; body: MutateWidgetPayload; companyId?: string | number | null }
    >
  ): MutationResult<
    WidgetDto,
    { dashboardId: string; body: MutateWidgetPayload; companyId?: string | number | null }
  > => {
    return useMutation<
      WidgetDto,
      AxiosError,
      { dashboardId: string; body: MutateWidgetPayload; companyId?: string | number | null }
    >(
      [QueryKeys.CreateWidget],
      ({
        dashboardId,
        body,
        companyId,
      }: {
        dashboardId: string;
        body: MutateWidgetPayload;
        companyId?: string | number | null;
      }) => this.createDashboardWidget(dashboardId, body, companyId),
      options
    );
  };

  public useUpdateDashboardWidget = (
    options?: MutationOptions<
      WidgetDto,
      {
        dashboardId: string;
        widgetId: string;
        body: MutateWidgetPayload;
        companyId?: string | number | null;
      }
    >
  ): MutationResult<
    WidgetDto,
    {
      dashboardId: string;
      widgetId: string;
      body: MutateWidgetPayload;
      companyId?: string | number | null;
    }
  > => {
    return useMutation<
      WidgetDto,
      AxiosError,
      {
        dashboardId: string;
        widgetId: string;
        body: MutateWidgetPayload;
        companyId?: string | number | null;
      }
    >(
      [QueryKeys.UpdateWidget],
      ({
        dashboardId,
        widgetId,
        body,
        companyId,
      }: {
        dashboardId: string;
        widgetId: string;
        body: MutateWidgetPayload;
        companyId?: string | number | null;
      }) => this.updateDashboardWidget(dashboardId, widgetId, body, companyId),
      options
    );
  };

  public useDeleteDashboardWidget = (
    options?: MutationOptions<
      boolean,
      { dashboardId: string; widgetId: string; companyId?: string | number | null }
    >
  ): MutationResult<
    boolean,
    { dashboardId: string; widgetId: string; companyId?: string | number | null }
  > => {
    return useMutation<
      boolean,
      AxiosError,
      { dashboardId: string; widgetId: string; companyId?: string | number | null }
    >(
      [QueryKeys.DeleteWidget],
      ({
        dashboardId,
        widgetId,
        companyId,
      }: {
        dashboardId: string;
        widgetId: string;
        companyId?: string | number | null;
      }) => this.deleteDashboardWidget(dashboardId, widgetId, companyId),
      options
    );
  };

  private getDashboardWidgets = async (
    dashboardId: string,
    companyId?: string | number | null
  ): Promise<WidgetDto[]> => {
    return await this.axios
      .get(buildDashboardUrl(`/dashboard/api/dashboards/${dashboardId}/widgets`), {
        params: companyId != null ? { companyId } : {},
      })
      .then((response: AxiosResponse<WidgetDto[]>) => response.data);
  };

  private getDashboardWidget = async (
    dashboardId: string,
    widgetId: string,
    companyId?: string | number | null
  ): Promise<WidgetDto> => {
    return await this.axios
      .get(buildDashboardUrl(`/dashboard/api/dashboards/${dashboardId}/widgets/${widgetId}`), {
        params: companyId != null ? { companyId } : {},
      })
      .then((response: AxiosResponse<WidgetDto>) => response.data);
  };

  private createDashboardWidget = async (
    dashboardId: string,
    body: MutateWidgetPayload,
    companyId?: string | number | null
  ): Promise<WidgetDto> => {
    return await this.axios
      .post(buildDashboardUrl(`/dashboard/api/dashboards/${dashboardId}/widgets`), body, {
        params: companyId != null ? { companyId } : {},
      })
      .then((response: AxiosResponse<WidgetDto>) => response.data);
  };

  private updateDashboardWidget = async (
    dashboardId: string,
    widgetId: string,
    body: MutateWidgetPayload,
    companyId?: string | number | null
  ): Promise<WidgetDto> => {
    return await this.axios
      .put(
        buildDashboardUrl(`/dashboard/api/dashboards/${dashboardId}/widgets/${widgetId}`),
        body,
        {
          params: companyId != null ? { companyId } : {},
        }
      )
      .then((response: AxiosResponse<WidgetDto>) => response.data);
  };

  private deleteDashboardWidget = async (
    dashboardId: string,
    widgetId: string,
    companyId?: string | number | null
  ): Promise<boolean> => {
    return await this.axios
      .delete(buildDashboardUrl(`/dashboard/api/dashboards/${dashboardId}/widgets/${widgetId}`), {
        params: companyId != null ? { companyId } : {},
      })
      .then((response: AxiosResponse<boolean>) => response.data);
  };
}

export interface DashboardWidgetQueryClient {
  useDashboardWidgets: (
    dashboardId: string,
    companyId?: string | number | null,
    options?: QueryOptions<WidgetDto[]>
  ) => QueryResult<WidgetDto[]>;
  useDashboardWidget: (
    dashboardId: string,
    widgetId: string,
    companyId?: string | number | null,
    options?: QueryOptions<WidgetDto>
  ) => QueryResult<WidgetDto>;
  useCreateDashboardWidget: (
    options?: MutationOptions<
      WidgetDto,
      { dashboardId: string; body: MutateWidgetPayload; companyId?: string | number | null }
    >
  ) => MutationResult<
    WidgetDto,
    { dashboardId: string; body: MutateWidgetPayload; companyId?: string | number | null }
  >;
  useUpdateDashboardWidget: (
    options?: MutationOptions<
      WidgetDto,
      {
        dashboardId: string;
        widgetId: string;
        body: MutateWidgetPayload;
        companyId?: string | number | null;
      }
    >
  ) => MutationResult<
    WidgetDto,
    {
      dashboardId: string;
      widgetId: string;
      body: MutateWidgetPayload;
      companyId?: string | number | null;
    }
  >;
  useDeleteDashboardWidget: (
    options?: MutationOptions<
      boolean,
      { dashboardId: string; widgetId: string; companyId?: string | number | null }
    >
  ) => MutationResult<
    boolean,
    { dashboardId: string; widgetId: string; companyId?: string | number | null }
  >;
}
